import { useCallback, useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSync } from "@/lib/live";
import { Panel, Stat, inr } from "@/admin/ui";
import { MapCanvas, itemsOf, useMapData, LAYERS } from "@/admin/AdminMap";

const ALERTS = [["overdue", "Overdue pickups: past their time slot", "live"], ["missed_awaiting", "Partner couldn't complete: waiting for customer", "live"],
  ["needs_admin", "No partner accepted: assign now", "live"], ["new_bookings", "New pickups to confirm", "bookings"], ["awaiting_reply", "Complaints awaiting reply", "complaints"],
  ["chats_waiting", "Pickup chats waiting", "chats"], ["kyc_pending", "KYC to verify", "kyc"], ["open_complaints", "Open complaints", "complaints"]];
const ON = Object.fromEntries(LAYERS.map(([k]) => [k, k !== "radius"]));

const Hero = ({ d, go }) => (
  <div className="ks-rise relative overflow-hidden rounded-2xl bg-[#0D3B2B] p-6 text-white lg:p-8" data-testid="overview-hero">
    <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-[#2E9E6B]/30 blur-3xl" />
    <p className="text-[11px] font-bold uppercase tracking-[.2em] text-white/60">Kindsaver operations · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
    <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
      <div><h2 className="ks-serif text-3xl sm:text-4xl">Today: {d.today.completed} of {d.today.pickups} pickups done.</h2>
        <p className="mt-1 text-sm text-white/75">{d.today.kg} kg collected · {d.today.active_partners} kabadiwalas online · {d.week.completed} pickups this week</p></div>
      <button onClick={() => go("map")} data-testid="overview-open-map" className="inline-flex h-11 items-center gap-2 rounded-full bg-white px-5 text-sm font-bold text-[#0D3B2B] transition-transform hover:translate-x-0.5">Open live map<ArrowRight className="h-4 w-4" /></button>
    </div>
  </div>
);

const Attention = ({ a, go }) => {
  const rows = ALERTS.filter(([k]) => a[k]);
  return (
    <Panel title="Needs attention" sub="Click to jump straight to the work" testid="overview-alerts">
      {rows.length ? <div className="space-y-2">{rows.map(([k, l, s]) => (
        <button key={k} onClick={() => go(s)} data-testid={`overview-alert-${k}`} className="flex w-full items-center justify-between rounded-xl border border-[#F3D5CC] bg-[#FDF4F1] px-4 py-3 text-left transition-colors hover:bg-[#FBE9E3]">
          <span className="flex items-center gap-2 text-sm font-semibold text-[#0D3B2B]"><AlertTriangle className="h-4 w-4 text-[#E05C3A]" />{l}</span>
          <b className="ks-serif text-xl text-[#E05C3A]">{a[k]}</b>
        </button>))}</div>
        : <p className="flex items-center gap-2 text-sm font-semibold text-[#2E9E6B]"><CheckCircle2 className="h-5 w-5" />All clear. Nothing is waiting on the team.</p>}
    </Panel>
  );
};

const Trend = ({ rows }) => (
  <Panel title="Last 7 days" sub="Pickups completed and value paid to customers" testid="overview-trend">
    <div className="h-64">
      <ResponsiveContainer>
        <ComposedChart data={rows.map((r) => ({ ...r, day: new Date(r.date).toLocaleDateString("en-IN", { weekday: "short" }) }))}>
          <CartesianGrid stroke="#EEF0E9" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#6B7F74" }} axisLine={false} tickLine={false} />
          <YAxis yAxisId="l" allowDecimals={false} tick={{ fontSize: 12, fill: "#6B7F74" }} axisLine={false} tickLine={false} width={28} />
          <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 12, fill: "#6B7F74" }} axisLine={false} tickLine={false} width={48} />
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E3E6DD" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="l" dataKey="booked" name="Booked" fill="#CFE7DA" radius={[6, 6, 0, 0]} />
          <Bar yAxisId="l" dataKey="completed" name="Completed" fill="#2E9E6B" radius={[6, 6, 0, 0]} />
          <Line yAxisId="r" dataKey="revenue" name="Value ₹" stroke="#E05C3A" strokeWidth={2.5} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </Panel>
);

const MiniMap = ({ go }) => {
  const d = useMapData();
  const items = useMemo(() => itemsOf(d), [d]);
  return (
    <Panel title="Live map" sub="Tap any dot for details" action={<button onClick={() => go("map")} className="text-sm font-bold text-[#2E9E6B]" data-testid="overview-map-expand">Full screen</button>} testid="overview-minimap">
      <div className="h-64 overflow-hidden rounded-xl">{d ? <MapCanvas items={items} on={ON} /> : <p className="text-sm text-[#6B7F74]">Loading…</p>}</div>
    </Panel>
  );
};

export const AdminOverview = ({ go, role }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/admin/overview").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);
  useSync(load);
  if (!d) return <p className="text-[#6B7F74]">Loading overview…</p>;
  const j = d.journey;
  const ops = role !== "kyc";
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <Hero d={d} go={go} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Today's pickups" value={`${d.today.completed}/${d.today.pickups}`} hint="completed / booked" tone="green" testid="kpi-pickups" />
        <Stat label="Pickup value today" value={inr(d.today.revenue)} hint={`${inr(d.week.revenue)} this week`} tone="ink" testid="kpi-revenue" />
        <Stat label="Commission earned" value={inr(d.today.commission)} hint={`${inr(d.week.commission)} this week`} tone="amber" testid="kpi-commission" />
        <Stat label="Active partners" value={`${d.today.active_partners}/${d.today.total_partners}`} hint="online with live GPS" tone="green" testid="kpi-partners" onClick={ops ? () => go("map") : undefined} />
      </div>
      <Panel title="Customer journey right now" sub="Updates automatically as bookings move" testid="overview-journey"
        action={ops && <button onClick={() => go("bookings")} className="h-9 rounded-full bg-[#0D3B2B] px-4 text-xs font-bold text-white" data-testid="overview-open-bookings">Open bookings</button>}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <Stat value={j.pending} label="Pending" hint="waiting to be confirmed" tone="amber" onClick={ops ? () => go("bookings") : undefined} testid="journey-pending" />
          <Stat value={j.overdue} label="Overdue" hint="date passed, still open" tone="coral" onClick={ops ? () => go("bookings") : undefined} testid="journey-overdue" />
          <Stat value={j.unassigned} label="Unassigned" hint="no partner yet" tone="coral" onClick={ops ? () => go("live") : undefined} testid="journey-unassigned" />
          <Stat value={j.at_risk} label="At risk" hint="no pickup in 30+ days" tone="amber" onClick={ops ? () => go("customers") : undefined} testid="journey-at-risk" />
          <Stat value={j.new_signups} label="New sign-ups" hint="joined in last 7 days" tone="green" onClick={ops ? () => go("customers") : undefined} testid="journey-signups" />
          <Stat value={j.customers} label="Customers" hint="total registered" tone="ink" onClick={ops ? () => go("customers") : undefined} testid="journey-customers" />
        </div>
      </Panel>
      <div className="grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <Trend rows={d.trend} />
        <Attention a={d.alerts} go={go} />
      </div>
      {ops && <MiniMap go={go} />}
    </div>
  );
};
